import React, { useState } from 'react';
import { austinInvasivePlants } from '../data/invasivePlants';
import { localResources } from '../data/resources';
import ResourceCard from '../components/ResourceCard';
import NurseriesSection from '../components/NurseriesSection';

interface ResourcesPageProps {
  setCurrentPage: (page: 'home' | 'upload' | 'collection' | 'about' | 'resources' | 'loading' | 'chat') => void;
}

type ResourcesSection = 'nurseries' | 'local-focus' | 'resources';

function ResourcesPage({ setCurrentPage }: ResourcesPageProps) {
  const [activeSection, setActiveSection] = useState<ResourcesSection>('nurseries');

  const sections: { id: ResourcesSection; title: string; icon: string }[] = [
    { id: 'nurseries', title: 'Nurseries', icon: '' },
    { id: 'local-focus', title: 'Local Focus', icon: '' },
    { id: 'resources', title: 'Resources', icon: '' },
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'local-focus':
        return (
          <div className="resources-section-content">
            <h2>Local Austin/Travis County Focus</h2>
            <div className="content-intro">
              <p>Learn about the most problematic invasive plants in our area and how to identify and manage them.</p>
            </div>

            <div className="plants-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {austinInvasivePlants.map((plant) => (
                <div key={plant.id} className="plant-list-item" style={{
                  padding: '2rem',
                  backgroundColor: 'var(--container-bg)',
                  borderRadius: 'var(--radius-card)',
                  boxShadow: 'var(--shadow-sm)',
                  border: '1px solid var(--border-subtle)'
                }}>
                  <div className="plant-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--primary)' }}>{plant.commonName}</h3>
                    <span style={{ fontSize: '0.9rem', fontStyle: 'italic', color: 'var(--text-secondary)' }}>{plant.scientificName}</span>
                  </div>
                  <p style={{ margin: 0, color: 'var(--text-primary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
                    {plant.description}
                  </p>
                  <div className="plant-details" style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    <strong>Key ID Feature:</strong> {plant.identificationTips[0]}
                  </div>
                </div>
              ))}
            </div>

            <div className="additional-info">
              <div className="info-card">
                <h3> Balcones Canyonlands Preserve</h3>
                <p>Our local preserve faces unique challenges from invasive species that threaten endangered species habitats. Conservation efforts focus on:</p>
                <ul>
                  <li>Protecting golden-cheeked warbler and black-capped vireo habitats</li>
                  <li>Managing invasive species in sensitive karst ecosystems</li>
                  <li>Restoring native plant communities</li>
                  <li>Monitoring water quality impacts</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'resources':
        return (
          <div className="resources-section-content">
            <h2>Resources and Programs</h2>
            <div className="content-intro">
              <p>Access local programs, guides, and organizations that can help you manage invasive species and restore native habitats.</p>
            </div>

            <div className="resources-grid">
              {localResources.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </div>
        );

      case 'nurseries':
        return <NurseriesSection />;


      default:
        return null;
    }
  };

  return (
    <section className="resources-section">
      <div className="container">
        <div className="resources-header">
          <h1>Resources</h1>
          <p className="subtitle">
            Comprehensive resources to help you understand, identify, and manage invasive species in your community.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="resources-tabs">
          {sections.map((section) => (
            <button
              key={section.id}
              className={`resources-tab ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="tab-icon">{section.icon}</span>
              <span className="tab-label">{section.title}</span>
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="resources-content">
          {renderSectionContent()}
        </div>

        {/* Call to Action */}
        <div className="resources-cta">
          <h3>Ready to Take Action?</h3>
          <p>Use our scanning feature to identify invasive plants in your area and contribute to conservation efforts.</p>
          <button
            className="button primary-button"
            onClick={() => setCurrentPage('upload')}
          >
            <span className="button-icon"></span>
            <span className="button-text">Start Scanning Plants</span>
          </button>
        </div>
      </div>
    </section>
  );
}

export default ResourcesPage;